import { useState, useEffect, useCallback } from 'react';
import { RecentSearchOption } from '~/components/OmniSearch/OmniSearch';

const STORAGE_KEY = 'omni-search-default-group-option2';
const MAX_HISTORY = 12;
const RECENT_SEARCHES_UPDATE_EVENT = 'recent-searches-update';

function loadFromStorage(): Array<RecentSearchOption> {
  try {
    const value = localStorage.getItem( STORAGE_KEY );

    if ( value ) {
      const parsed = JSON.parse( value );

      if ( parsed?.options ) {
        return parsed.options as Array<RecentSearchOption>;
      }
    }
  } catch ( e ) {
    localStorage.removeItem( STORAGE_KEY );
  }

  return [];
}

function saveToStorage( options: Array<RecentSearchOption> ): void {
  localStorage.setItem( STORAGE_KEY, JSON.stringify( { options } ) );
}

export function useRecentSearches() {
  const [recentSearches, setRecentSearches] = useState<Array<RecentSearchOption>>( loadFromStorage );

  const addRecentSearch = useCallback( ( newOption: RecentSearchOption ) => {
    setRecentSearches( ( oldOptions ) => {
      const recent = [...oldOptions];

      const index = recent.findIndex( ( option ) => option.value === newOption.value );

      if ( index > -1 ) {
        recent.splice( index, 1 );
      }

      recent.unshift( newOption );

      if ( recent.length > MAX_HISTORY ) {
        recent.pop();
      }

      saveToStorage( recent );

      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent( new CustomEvent( RECENT_SEARCHES_UPDATE_EVENT ) );

      return recent;
    } );
  }, [] );

  useEffect( () => {
    // Listen for storage changes from other tabs
    const handleStorageChange = ( event: StorageEvent ) => {
      if ( event.key === STORAGE_KEY ) {
        setRecentSearches( loadFromStorage() );
      }
    };

    // Listen for updates from same tab via custom event
    const handleCustomEvent = () => {
      setRecentSearches( loadFromStorage() );
    };

    window.addEventListener( 'storage', handleStorageChange );
    window.addEventListener( RECENT_SEARCHES_UPDATE_EVENT, handleCustomEvent );

    return () => {
      window.removeEventListener( 'storage', handleStorageChange );
      window.removeEventListener( RECENT_SEARCHES_UPDATE_EVENT, handleCustomEvent );
    };
  }, [] );

  return {
    recentSearches,
    addRecentSearch,
  };
}
